import * as React from 'react';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SxProps, Theme, CircularProgress, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * One option shape for every caller: `value` is always a string, normally an id.
 *
 * Passing a whole object as `value` does not work — MUI matches the selected
 * option with `String(a) === String(b)` for primitives but falls back to
 * reference identity for objects, so an object option never matches a string
 * `selected` and MUI logs an out-of-range warning with nothing highlighted.
 * Callers that need the full record should look it up by id in their onChange.
 */
export interface SelectOption {
	value: string;
	label: string;
	disabled?: boolean;
}

interface SelectComponentProps {
	data: SelectOption[];
	label: string;
	/** Controlled value. Use '' for "nothing selected". */
	selected?: string;
	sx?: SxProps<Theme>;
	error?: boolean;
	errorMessage?: string;
	disabled?: boolean;
	loading?: boolean;
	onChange?: (value: string) => void;
	required?: boolean;
}

export const SelectComponent = (props: SelectComponentProps) => {
	const {
		data,
		label,
		disabled = false,
		loading = false,
		error = false,
		errorMessage,
		required = false,
		onChange,
	} = props;

	/* Fully controlled: no local copy of `selected` to drift out of sync. */
	const value = props.selected ?? '';

	/*
	 * MUI hides the rendered value when it is empty, so an explicit ''-valued
	 * option (e.g. "All") would show a blank box once chosen. Turn on
	 * displayEmpty for exactly those selects, and pin the label shrunk so it
	 * does not sit on top of the option text.
	 */
	const hasEmptyOption = data.some((item) => item.value === '');

	const handleChange = (event: SelectChangeEvent) => {
		onChange?.(event.target.value);
	};

	const renderValue = (current: string) => {
		if (loading) {
			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<CircularProgress size={16} />
					<Typography variant="body1" color="text.secondary">
						Loading...
					</Typography>
				</Box>
			);
		}
		return data.find((item) => item.value === current)?.label ?? '';
	};

	return (
		<Box sx={{ minWidth: 120, ...props.sx }}>
			<FormControl
				fullWidth
				error={error}
				required={required}
			>
				<InputLabel
					id={`${label}-label`}
					error={error}
					shrink={hasEmptyOption ? true : undefined}
				>
					{label}
				</InputLabel>
				<Select
					labelId={`${label}-label`}
					id={label}
					value={value}
					label={label}
					displayEmpty={hasEmptyOption}
					notched={hasEmptyOption ? true : undefined}
					onChange={handleChange}
					error={error}
					disabled={disabled || loading}
					renderValue={renderValue}
					IconComponent={loading ? () => <CircularProgress size={20} /> : undefined}
					sx={{
						'.MuiSelect-icon': {
							right: error ? 32 : 12
						}
					}}
				>
					{error ? (
						<MenuItem disabled>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
								<ErrorOutlineIcon fontSize="small" />
								<Typography variant="body2">
									{errorMessage || 'An error occurred'}
								</Typography>
							</Box>
						</MenuItem>
					) : data.length === 0 ? (
						<MenuItem disabled>
							<Typography variant="body2" color="text.secondary">
								No options available
							</Typography>
						</MenuItem>
					) : (
						/* Keyed on value, not label — two locations can share a city name. */
						data.map((item) => (
							<MenuItem value={item.value} key={item.value} disabled={item.disabled}>
								{item.label}
							</MenuItem>
						))
					)}
				</Select>
			</FormControl>
		</Box>
	);
};
